import React, { useContext, useEffect, useMemo, useState } from "react";
import { message } from "antd";

import RebetLoader from "../../CommonComponents/RebetLoader/RebetLoader";
import { ToggleSwitch } from "../../shared";
import AppContext from "../../../context/AppContext";
import { SIDER_TABS } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { MAINTENANCE_MODE_PERMISSIONS } from "../../../utils/constants/permissionsConstants";
import {
  useMaintenanceMode,
  useUpdateMaintenanceMode,
  type MaintenanceModeChannel,
  type MaintenanceModeStatus,
} from "../../../queries/maintenance-mode/useMaintenanceMode";

import styles from "./MaintenanceMode.module.css";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

const FALLBACK_STATUS: MaintenanceModeStatus = { web: false, mobile: false };

type MaintenanceModeAppContext = {
  setActiveSidebarTab: (tab: string, option?: string) => void;
  adminPermissions?: string[];
};

const MaintenanceMode: React.FC = () => {
  usePageTitle(AppStrings.MAINTENANCE_MODE);

  const { setActiveSidebarTab, adminPermissions } = useContext(
    AppContext,
  ) as MaintenanceModeAppContext;
  const { data, isLoading, isError, isFetching } = useMaintenanceMode();
  const updateMutation = useUpdateMaintenanceMode();

  const canUpdate = useMemo(() => {
    const permissions = adminPermissions ?? [];
    return permissions.includes(MAINTENANCE_MODE_PERMISSIONS.UPDATE);
  }, [adminPermissions]);

  // Local mirror so a failed PATCH can snap the switch back without waiting
  // for a refetch. Synced from query data whenever it changes.
  const [status, setStatus] = useState<MaintenanceModeStatus | null>(null);
  const [pendingChannel, setPendingChannel] =
    useState<MaintenanceModeChannel | null>(null);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.COMPLIANCE, "MAINTENANCE_MODE");
  }, [setActiveSidebarTab]);

  useEffect(() => {
    if (data) setStatus(data);
  }, [data]);

  // Load failure with no cached status: still render toggles (off) but
  // keep them disabled so admins don't PATCH against an unknown state.
  const loadFailed = isError && !status;
  const displayStatus = status ?? FALLBACK_STATUS;
  const togglesDisabled =
    loadFailed || !canUpdate || pendingChannel !== null;

  useEffect(() => {
    if (!loadFailed) return;
    message.error(AppStrings.MAINTENANCE_MODE_LOAD_ERROR);
  }, [loadFailed]);

  const handleToggle = async (
    channel: MaintenanceModeChannel,
    next: boolean,
  ) => {
    if (!status || !canUpdate || loadFailed) return;
    const previous = status;
    const optimistic: MaintenanceModeStatus = { ...status, [channel]: next };
    setStatus(optimistic);
    setPendingChannel(channel);
    try {
      const saved = await updateMutation.mutateAsync({ [channel]: next });
      setStatus(saved);
      message.success(AppStrings.MAINTENANCE_MODE_UPDATE_SUCCESS);
    } catch {
      setStatus(previous);
      message.error(AppStrings.MAINTENANCE_MODE_UPDATE_ERROR);
    } finally {
      setPendingChannel(null);
    }
  };

  return (
    <>
      <div className={styles.page} data-testid="maintenance-mode-page">
        <div className={styles.content}>
          <p className={styles.description}>
            {AppStrings.MAINTENANCE_MODE_DESCRIPTION}
          </p>

          {isLoading || (isFetching && !status && !isError) ? (
            <div
              className={styles.loadingWrap}
              data-testid="maintenance-mode-loading"
            >
              <RebetLoader height="64px" width="64px" />
            </div>
          ) : (
            <div className={styles.card} data-testid="maintenance-mode-toggles">
              <div className={styles.row}>
                <span className={styles.label}>
                  {AppStrings.MAINTENANCE_MODE_WEB_APP}
                </span>
                <ToggleSwitch
                  value={displayStatus.web}
                  disabled={togglesDisabled}
                  onChange={(checked) => handleToggle("web", checked)}
                  ariaLabel={AppStrings.MAINTENANCE_MODE_WEB_APP}
                />
              </div>
              <div className={styles.row}>
                <span className={styles.label}>
                  {AppStrings.MAINTENANCE_MODE_MOBILE_APP}
                </span>
                <ToggleSwitch
                  value={displayStatus.mobile}
                  disabled={togglesDisabled}
                  onChange={(checked) => handleToggle("mobile", checked)}
                  ariaLabel={AppStrings.MAINTENANCE_MODE_MOBILE_APP}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MaintenanceMode;
